import { useMemo } from "react";
import { useRequest } from "@/request";
import type { PagedResultDto } from "@/types/abp";

/** 工单渠道（对应后端 TicketChannel）。 */
export const TicketChannel = {
  Web: 1,
  Phone: 2,
  Api: 3,
} as const;
export type TicketChannel = (typeof TicketChannel)[keyof typeof TicketChannel];

/** 工单状态（对应后端 TicketStatus）。 */
export const TicketStatus = {
  New: 1,
  Open: 2,
  Pending: 3,
  Resolved: 4,
  Closed: 5,
  Cancelled: 6,
} as const;
export type TicketStatus = (typeof TicketStatus)[keyof typeof TicketStatus];

/** 工单优先级（对应后端 TicketPriority）。 */
export const TicketPriority = {
  Low: 1,
  Normal: 2,
  High: 3,
  Urgent: 4,
} as const;
export type TicketPriority = (typeof TicketPriority)[keyof typeof TicketPriority];

/** 展示用的成员引用：名字与头像随工单一起返回，不必再单独拉成员表。 */
export type TicketAgentRefDto = {
  agentId: string;
  displayName: string;
  avatarUrl: string | null;
};

/** 工单（对应 Api.Admin TicketDto，JSON 为 camelCase）。 */
export type TicketDto = {
  id: string;
  number: string;
  subject: string;
  body: string | null;
  channel: TicketChannel;
  status: TicketStatus;
  priority: TicketPriority;
  projectId: string;
  projectCode: string | null;
  projectName: string | null;
  categoryId: string | null;
  categoryName: string | null;
  /** 分类的完整路径（含上级）。 */
  categoryPath: string | null;
  customerId: string | null;
  assignedTeamId: string | null;
  assignedTeamName: string | null;
  assigneeId: string | null;
  assignee: TicketAgentRefDto | null;
  firstResponseAt: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  /** 消息条数（回复与内部备注都算）；旧版服务未返回时前端按 0 展示。 */
  messageCount?: number | null;
  creationTime: string;
  /** 最近一次修改时间；从未修改时可能为空，列表回退到创建时间。 */
  lastModificationTime?: string | null;
  concurrencyStamp: string;
};

/** 工单详情：比列表多一份协作者名单。 */
export type TicketDetailDto = TicketDto & {
  participants: TicketAgentRefDto[];
  /** 建单或重新关联客户时保存的联系人快照。 */
  requester: TicketRequesterDto | null;
  /** 当前关联客户资料。 */
  customer: TicketCustomerDto | null;
  /** 业务系统建单时透传的扩展信息（原始 JSON）；列表不返回，只有详情有。 */
  extendedInfoJson: string | null;
};

export type TicketRequesterDto = {
  name: string | null;
  phone: string | null;
  email: string | null;
};

export type TicketCustomerDto = TicketRequesterDto & {
  id: string;
  businessUserReference: string | null;
  extendedInfoJson: string | null;
};

export type TicketLinkedEntityDto = {
  id: string;
  systemCode: string;
  entityType: string;
  externalId: string;
  extendedInfoJson: string | null;
  creationTime: string;
  data: Record<string, unknown> | null;
  resolutionStatus: "unhandled" | "resolved" | "not_found" | "unavailable";
  contents: Array<{ key: string; fileName: string; contentType: string; size: number | null }>;
};

/** 消息类型（对应后端 TicketMessageKind）。工单对客户不可见，界面只用 Reply。 */
export const TicketMessageKind = {
  Reply: 1,
  InternalNote: 2,
} as const;
export type TicketMessageKind = (typeof TicketMessageKind)[keyof typeof TicketMessageKind];

/** 正文里被提及的成员；displayName 由服务端按当前资料填充，改名自动跟随。 */
export type TicketMessageMentionDto = {
  agentId: string;
  displayName: string;
};

/** 附件扫描状态（对应后端 AttachmentScanStatus）。只有 Clean 才可下载、可随消息发出。 */
export const AttachmentScanStatus = {
  Quarantined: 1,
  Scanning: 2,
  Clean: 3,
  Rejected: 4,
} as const;
export type AttachmentScanStatus = (typeof AttachmentScanStatus)[keyof typeof AttachmentScanStatus];

/** 工单附件元数据（对应 Api.Admin TicketAttachmentDto）；文件本体在对象存储里，按需下载。 */
export type TicketAttachmentDto = {
  id: string;
  ticketId: string;
  /** 已随消息发出时为该消息 id；仅上传尚未发出的草稿附件为 null。 */
  messageId: string | null;
  fileName: string;
  /** 服务端按扩展名白名单定下的规范类型，不是上传时客户端声明的那个。 */
  contentType: string;
  size: number;
  scanStatus: AttachmentScanStatus;
  scanFailureReason: string | null;
  creationTime: string;
};

/** 工单消息（对应 Api.Admin TicketMessageDto）。 */
export type TicketMessageDto = {
  id: string;
  ticketId: string;
  authorId: string | null;
  authorDisplayName: string | null;
  authorAvatarUrl: string | null;
  kind: TicketMessageKind;
  /** 正文；@提及以 `@{agentId}` token 内嵌，用 mentionToken/ 渲染层还原。 */
  body: string;
  mentions: TicketMessageMentionDto[];
  /** 随这条消息发出的附件；文件本体经附件下载接口取。 */
  attachments: TicketAttachmentDto[];
  creationTime: string;
};

export type AddTicketMessageInput = {
  kind?: TicketMessageKind;
  body: string;
  /** 随消息发出的附件 id，取自 `uploadAttachment` 的返回值。 */
  attachmentIds?: string[];
};

/** 操作者类型（对应后端 TicketActorType）。 */
export const TicketActorType = {
  Agent: 1,
  ApiClient: 2,
  System: 3,
  Anonymous: 4,
} as const;
export type TicketActorType = (typeof TicketActorType)[keyof typeof TicketActorType];

/** 时间线条目种类（对应后端 TicketTimelineEntryKind）。 */
export const TicketTimelineEntryKind = {
  Event: 1,
  Message: 2,
} as const;
export type TicketTimelineEntryKind = (typeof TicketTimelineEntryKind)[keyof typeof TicketTimelineEntryKind];

/** 事件动作，与后端 `TicketEventActions` 常量一一对应。 */
export const TicketEventAction = {
  Created: "created",
  ContentUpdated: "content_updated",
  PriorityChanged: "priority_changed",
  CategoryChanged: "category_changed",
  CustomerChanged: "customer_changed",
  Routed: "routed",
  Assigned: "assigned",
  Unassigned: "unassigned",
  StatusChanged: "status_changed",
  ParticipantAdded: "participant_added",
  ParticipantRemoved: "participant_removed",
  MessageAdded: "message_added",
  AttachmentAdded: "attachment_added",
  TagAdded: "tag_added",
  TagRemoved: "tag_removed",
} as const;
export type TicketEventAction = (typeof TicketEventAction)[keyof typeof TicketEventAction];

/** 每次进入已解决状态时保存的独立记录。 */
export type TicketResolutionDto = {
  id: string;
  ticketId: string;
  resolutionResult: string;
  feedbackMethod: TicketFeedbackMethod;
  feedbackMethodOther: string | null;
  resolvedById: string | null;
  resolvedAt: string;
};

/**
 * 时间线的一条（对应 Api.Admin TicketTimelineEntryDto）。
 * 事件与消息已由服务端归并排好序，`message_added` 直接展开成 `Message` 条目，
 * 不需要再单独取 messages 自行合并。
 */
export type TicketTimelineEntryDto = {
  id: string;
  kind: TicketTimelineEntryKind;
  occurredAt: string;
  actorType: TicketActorType;
  actorId: string | null;
  actorDisplayName: string | null;
  actorAvatarUrl: string | null;
  /** 事件动作；消息条目为 null。 */
  action: TicketEventAction | null;
  /** 原始值：枚举为小写名（`open`/`urgent`），实体为 id。 */
  fromValue: string | null;
  toValue: string | null;
  /** 值是实体 id 时（分类/组/成员/客户/标签）服务端解析好的名字，直接显示即可。 */
  fromDisplayName: string | null;
  toDisplayName: string | null;
  detailJson: string | null;
  /** 本次状态事件关联的解决记录；旧服务及普通事件可能不返回。 */
  resolution?: TicketResolutionDto | null;
  /** 消息条目的正文与提及；事件条目为 null。 */
  message: TicketMessageDto | null;
};

/**
 * @提及在正文里的序列化格式，与后端 TicketMentionFormat 一一对应。
 * 存的是 AgentId 而非显示名：成员改名后历史消息自动跟随，重名也不会歧义。
 */
export function mentionToken(agentId: string): string {
  return `@{${agentId}}`;
}

/** @提及候选里的一名成员。 */
export type TicketMentionCandidateDto = {
  agentId: string;
  displayName: string;
  /** IAM 登录名。同名同邮箱的人靠它区分。 */
  userName: string;
  email: string | null;
  avatarUrl: string | null;
  /** 工单当前跟进人。 */
  isAssignee: boolean;
  /** 已经是协作者：提及他不会再改变协作者列表。 */
  isParticipant: boolean;
};

/** @提及候选的一个分组；teamId 为空表示尚未编入任何在用客服组的成员。 */
export type TicketMentionCandidateGroupDto = {
  teamId: string | null;
  teamName: string | null;
  isAssignedTeam: boolean;
  /** 本组还有未返回的匹配成员，靠继续输入收窄而不是翻页。 */
  hasMore: boolean;
  agents: TicketMentionCandidateDto[];
};

export type TicketMentionCandidateRequest = {
  filter?: string;
  maxPerGroup?: number;
};

/** 改分类入参；categoryId 传 null 表示置空（撤销分类）。 */
export type ChangeTicketCategoryInput = {
  categoryId: string | null;
  concurrencyStamp: string;
};

/** 批量改分类入参；分类是覆盖写，服务端不校验 concurrencyStamp。 */
export type BulkChangeTicketCategoryInput = {
  ticketIds: string[];
  categoryId: string | null;
};

/** 批量操作的逐条结果；整批不是一个事务，失败的那几条单独提示即可。 */
export type BulkTicketOperationResult = {
  succeeded: string[];
  failed: BulkTicketOperationFailure[];
};

export type BulkTicketOperationFailure = {
  ticketId: string;
  code: string;
  message: string | null;
};

export type ChangeTicketPriorityInput = {
  priority: TicketPriority;
  concurrencyStamp: string;
};

export type TicketFeedbackMethod = "phone" | "sms" | "email" | "other";

export type TicketResolutionInput = {
  resolutionResult: string;
  feedbackMethod: TicketFeedbackMethod;
  feedbackMethodOther?: string;
};

export type ChangeTicketStatusInput = {
  targetStatus: TicketStatus;
  concurrencyStamp: string;
  resolutionResult?: string;
  feedbackMethod?: TicketFeedbackMethod;
  feedbackMethodOther?: string;
};

export type ClaimTicketInput = {
  concurrencyStamp: string;
};

export type RouteTicketInput = {
  teamId: string;
  concurrencyStamp: string;
};

export type AssignTicketInput = {
  assigneeId: string;
  concurrencyStamp: string;
};

export type UnassignTicketInput = {
  concurrencyStamp: string;
};

/** 工单筛选条件；列表与计数共用（对应后端 TicketFilterRequest）。 */
export type TicketFilterRequest = {
  filter?: string;
  searchTerms?: string[];
  subject?: string;
  number?: string;
  /** 状态集合；后端按 IN 过滤（对应后端 Statuses，多值以重复查询参数传递）。 */
  statuses?: TicketStatus[];
  priorities?: TicketPriority[];
  customerId?: string | null;
  projectId?: string | null;
  projectCode?: string;
  categoryId?: string | null;
  assignedTeamId?: string | null;
  assigneeId?: string | null;
  /** 我经办：身份由服务端从登录态解析，无需传自己的 AgentId。 */
  assignedToMe?: boolean;
  /** 我参与：当前坐席在参与者列表中。 */
  participatingByMe?: boolean;
  unassigned?: boolean;
  /** 只看还没有分类的工单（待分类队列）。 */
  uncategorized?: boolean;
};

export type TicketListRequest = TicketFilterRequest & {
  skipCount?: number;
  maxResultCount?: number;
  sorting?: string;
};

/** 「我的视图」维度的工单数；口径与列表接口的同名筛选位一致。 */
export type TicketViewCounts = {
  all: number;
  assignedToMe: number;
  participatingByMe: number;
  unassigned: number;
  uncategorized: number;
};

export type TicketStatusCount = {
  status: TicketStatus;
  count: number;
};

function buildQuery(request: TicketListRequest): string {
  const params = new URLSearchParams();
  if (request.filter) {
    params.set("Filter", request.filter);
  }
  for (const searchTerm of request.searchTerms ?? []) {
    params.append("SearchTerms", searchTerm);
  }
  if (request.subject) {
    params.set("Subject", request.subject);
  }
  if (request.number) {
    params.set("Number", request.number);
  }
  for (const status of request.statuses ?? []) {
    params.append("Statuses", String(status));
  }
  for (const priority of request.priorities ?? []) {
    params.append("Priorities", String(priority));
  }
  if (request.customerId) {
    params.set("CustomerId", request.customerId);
  }
  if (request.projectId) {
    params.set("ProjectId", request.projectId);
  }
  if (request.projectCode) {
    params.set("ProjectCode", request.projectCode);
  }
  if (request.categoryId) {
    params.set("CategoryId", request.categoryId);
  }
  if (request.assignedTeamId) {
    params.set("AssignedTeamId", request.assignedTeamId);
  }
  if (request.assigneeId) {
    params.set("AssigneeId", request.assigneeId);
  }
  if (request.assignedToMe !== undefined) {
    params.set("AssignedToMe", String(request.assignedToMe));
  }
  if (request.participatingByMe !== undefined) {
    params.set("ParticipatingByMe", String(request.participatingByMe));
  }
  if (request.unassigned !== undefined) {
    params.set("Unassigned", String(request.unassigned));
  }
  if (request.uncategorized !== undefined) {
    params.set("Uncategorized", String(request.uncategorized));
  }
  if (request.skipCount !== undefined) {
    params.set("SkipCount", String(request.skipCount));
  }
  if (request.maxResultCount !== undefined) {
    params.set("MaxResultCount", String(request.maxResultCount));
  }
  if (request.sorting) {
    params.set("Sorting", request.sorting);
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}

function buildMentionQuery(request: TicketMentionCandidateRequest): string {
  const params = new URLSearchParams();
  if (request.filter) {
    params.set("Filter", request.filter);
  }
  if (request.maxPerGroup !== undefined) {
    params.set("MaxPerGroup", String(request.maxPerGroup));
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}

/** 工单客户端（工作台列表/详情与 AI 建议采纳用）。 */
export function useTicketApi() {
  const { request } = useRequest();

  return useMemo(
    () => ({
      getList: (input: TicketListRequest = {}, signal?: AbortSignal) =>
        request<PagedResultDto<TicketDto>>(`tickets${buildQuery(input)}`, { method: "GET", signal, skipErrorToast: true }),
      /** 「我的视图」维度的数量统计；忽略请求里的 assignedToMe / participatingByMe / unassigned。 */
      getViewCounts: (input: TicketFilterRequest = {}, signal?: AbortSignal) =>
        request<TicketViewCounts>(`tickets/counts/views${buildQuery(input)}`, { method: "GET", signal, skipErrorToast: true }),
      /** 状态维度的数量统计；忽略请求里的 statuses，六个状态恒定返回。 */
      getStatusCounts: (input: TicketFilterRequest = {}, signal?: AbortSignal) =>
        request<TicketStatusCount[]>(`tickets/counts/statuses${buildQuery(input)}`, { method: "GET", signal, skipErrorToast: true }),
      get: (id: string, signal?: AbortSignal) => request<TicketDetailDto>(`tickets/${id}`, { method: "GET", signal, skipErrorToast: true }),
      /** 外部业务关联列表；Customer 仍由详情接口返回，不混入这里。 */
      getLinkedEntities: (id: string, signal?: AbortSignal) =>
        request<TicketLinkedEntityDto[]>(`tickets/${id}/linked-entities`, { method: "GET", signal, skipErrorToast: true }),
      downloadLinkedEntityContent: (id: string, linkId: string, contentKey: string, signal?: AbortSignal) =>
        request<Blob>(`tickets/${id}/linked-entities/${linkId}/contents/${encodeURIComponent(contentKey)}`, {
          method: "GET",
          responseType: "blob",
          signal,
          timeout: 0,
          skipErrorToast: true,
        }),
      changeStatus: (id: string, input: ChangeTicketStatusInput) =>
        request<TicketDto>(`tickets/${id}/status`, { method: "POST", data: input, skipErrorToast: true }),
      changePriority: (id: string, input: ChangeTicketPriorityInput) =>
        request<TicketDto>(`tickets/${id}/priority`, { method: "PUT", data: input, skipErrorToast: true }),
      claim: (id: string, input: ClaimTicketInput) => request<TicketDto>(`tickets/${id}/claim`, { method: "POST", data: input, skipErrorToast: true }),
      changeCategory: (id: string, input: ChangeTicketCategoryInput) =>
        request<TicketDto>(`tickets/${id}/category`, { method: "PUT", data: input, skipErrorToast: true }),
      /** 批量改分类；逐条独立成败，调用方按 failed 提示。 */
      bulkChangeCategory: (input: BulkChangeTicketCategoryInput) =>
        request<BulkTicketOperationResult>("tickets/bulk/category", { method: "PUT", data: input, skipErrorToast: true }),
      route: (id: string, input: RouteTicketInput) => request<TicketDto>(`tickets/${id}/route`, { method: "POST", data: input, skipErrorToast: true }),
      assign: (id: string, input: AssignTicketInput) => request<TicketDto>(`tickets/${id}/assign`, { method: "POST", data: input, skipErrorToast: true }),
      unassign: (id: string, input: UnassignTicketInput) => request<TicketDto>(`tickets/${id}/unassign`, { method: "POST", data: input, skipErrorToast: true }),
      getMessages: (id: string, signal?: AbortSignal) => request<TicketMessageDto[]>(`tickets/${id}/messages`, { method: "GET", signal, skipErrorToast: true }),
      getTimeline: (id: string, signal?: AbortSignal) =>
        request<TicketTimelineEntryDto[]>(`tickets/${id}/timeline`, { method: "GET", signal, skipErrorToast: true }),
      addMessage: (id: string, input: AddTicketMessageInput) => request<TicketMessageDto>(`tickets/${id}/messages`, { method: "POST", data: input }),
      getAttachments: (id: string, signal?: AbortSignal) =>
        request<TicketAttachmentDto[]>(`tickets/${id}/attachments`, { method: "GET", signal, skipErrorToast: true }),
      /**
       * 上传附件。落地即「草稿附件」，要再调 `addMessage` 带上返回的 id 才会出现在时间线里。
       * 错误交给编辑器按文件逐个展示（哪个文件失败、能否重试），不弹全局 toast。
       */
      uploadAttachment: (id: string, file: File, options: { onProgress?: (percent: number) => void; signal?: AbortSignal } = {}) => {
        const formData = new FormData();
        // 字段名必须是 file：对应 Controller 的 IFormFile file 形参。
        formData.append("file", file, file.name);

        return request<TicketAttachmentDto>(`tickets/${id}/attachments`, {
          method: "POST",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
          // 大文件按默认 20s 超时必然中断，交给调用方用 signal 控制取消。
          timeout: 0,
          signal: options.signal,
          skipErrorToast: true,
          onUploadProgress: (event) => {
            if (!options.onProgress || !event.total) return;
            options.onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
          },
        });
      },
      /**
       * 下载附件的字节。附件端点要带 Authorization，`<a href>` 直链拿不到，
       * 只能取回 Blob 再由调用方生成 object URL。
       */
      downloadAttachment: (id: string, attachmentId: string, signal?: AbortSignal) =>
        request<Blob>(`tickets/${id}/attachments/${attachmentId}/content`, {
          method: "GET",
          responseType: "blob",
          timeout: 0,
          signal,
          skipErrorToast: true,
        }),
      /** 撤掉尚未随消息发出的草稿附件；已发出的附件服务端会拒绝。 */
      deleteAttachment: (id: string, attachmentId: string) =>
        request<void>(`tickets/${id}/attachments/${attachmentId}`, { method: "DELETE", skipErrorToast: true }),
      getMentionCandidates: (id: string, input: TicketMentionCandidateRequest = {}, signal?: AbortSignal) =>
        request<TicketMentionCandidateGroupDto[]>(`tickets/${id}/mention-candidates${buildMentionQuery(input)}`, {
          method: "GET",
          signal,
          skipErrorToast: true,
        }),
    }),
    [request],
  );
}
